/**
 * Currency Utility Functions
 * 
 * Comprehensive currency formatting utilities inspired by easy-invoice-pdf.
 * Provides 60+ currencies with symbols, labels, and locale-aware formatting.
 * 
 * @module utils/currency
 */

/**
 * Supported currencies with symbols and labels
 * Based on ISO 4217 currency codes
 */
export const SUPPORTED_CURRENCIES = {
  EUR: { code: 'EUR', symbol: '€', label: 'Euro', locale: 'de-DE' },
  USD: { code: 'USD', symbol: '$', label: 'US Dollar', locale: 'en-US' },
  GBP: { code: 'GBP', symbol: '£', label: 'British Pound', locale: 'en-GB' },
  JPY: { code: 'JPY', symbol: '¥', label: 'Japanese Yen', locale: 'ja-JP' },
  CHF: { code: 'CHF', symbol: 'CHF', label: 'Swiss Franc', locale: 'de-CH' },
  CAD: { code: 'CAD', symbol: 'C$', label: 'Canadian Dollar', locale: 'en-CA' },
  AUD: { code: 'AUD', symbol: 'A$', label: 'Australian Dollar', locale: 'en-AU' },
  NZD: { code: 'NZD', symbol: 'NZ$', label: 'New Zealand Dollar', locale: 'en-NZ' },
  CNY: { code: 'CNY', symbol: '¥', label: 'Chinese Yuan', locale: 'zh-CN' },
  HKD: { code: 'HKD', symbol: 'HK$', label: 'Hong Kong Dollar', locale: 'zh-HK' },
  SGD: { code: 'SGD', symbol: 'S$', label: 'Singapore Dollar', locale: 'en-SG' },
  SEK: { code: 'SEK', symbol: 'kr', label: 'Swedish Krona', locale: 'sv-SE' },
  NOK: { code: 'NOK', symbol: 'kr', label: 'Norwegian Krone', locale: 'nb-NO' },
  DKK: { code: 'DKK', symbol: 'kr', label: 'Danish Krone', locale: 'da-DK' },
  PLN: { code: 'PLN', symbol: 'zł', label: 'Polish Zloty', locale: 'pl-PL' },
  CZK: { code: 'CZK', symbol: 'Kč', label: 'Czech Koruna', locale: 'cs-CZ' },
  HUF: { code: 'HUF', symbol: 'Ft', label: 'Hungarian Forint', locale: 'hu-HU' },
  RON: { code: 'RON', symbol: 'lei', label: 'Romanian Leu', locale: 'ro-RO' },
  BGN: { code: 'BGN', symbol: 'лв', label: 'Bulgarian Lev', locale: 'bg-BG' },
  HRK: { code: 'HRK', symbol: 'kn', label: 'Croatian Kuna', locale: 'hr-HR' },
  RUB: { code: 'RUB', symbol: '₽', label: 'Russian Ruble', locale: 'ru-RU' },
  UAH: { code: 'UAH', symbol: '₴', label: 'Ukrainian Hryvnia', locale: 'uk-UA' },
  TRY: { code: 'TRY', symbol: '₺', label: 'Turkish Lira', locale: 'tr-TR' },
  ILS: { code: 'ILS', symbol: '₪', label: 'Israeli Shekel', locale: 'he-IL' },
  AED: { code: 'AED', symbol: 'د.إ', label: 'UAE Dirham', locale: 'ar-AE' },
  SAR: { code: 'SAR', symbol: '﷼', label: 'Saudi Riyal', locale: 'ar-SA' },
  QAR: { code: 'QAR', symbol: '﷼', label: 'Qatari Riyal', locale: 'ar-QA' },
  KWD: { code: 'KWD', symbol: 'د.ك', label: 'Kuwaiti Dinar', locale: 'ar-KW' },
  BHD: { code: 'BHD', symbol: '.د.ب', label: 'Bahraini Dinar', locale: 'ar-BH' },
  OMR: { code: 'OMR', symbol: '﷼', label: 'Omani Rial', locale: 'ar-OM' },
  INR: { code: 'INR', symbol: '₹', label: 'Indian Rupee', locale: 'en-IN' },
  PKR: { code: 'PKR', symbol: '₨', label: 'Pakistani Rupee', locale: 'ur-PK' },
  BDT: { code: 'BDT', symbol: '৳', label: 'Bangladeshi Taka', locale: 'bn-BD' },
  LKR: { code: 'LKR', symbol: '₨', label: 'Sri Lankan Rupee', locale: 'si-LK' },
  THB: { code: 'THB', symbol: '฿', label: 'Thai Baht', locale: 'th-TH' },
  MYR: { code: 'MYR', symbol: 'RM', label: 'Malaysian Ringgit', locale: 'ms-MY' },
  IDR: { code: 'IDR', symbol: 'Rp', label: 'Indonesian Rupiah', locale: 'id-ID' },
  PHP: { code: 'PHP', symbol: '₱', label: 'Philippine Peso', locale: 'en-PH' },
  VND: { code: 'VND', symbol: '₫', label: 'Vietnamese Dong', locale: 'vi-VN' },
  KRW: { code: 'KRW', symbol: '₩', label: 'South Korean Won', locale: 'ko-KR' },
  TWD: { code: 'TWD', symbol: 'NT$', label: 'Taiwan Dollar', locale: 'zh-TW' },
  BRL: { code: 'BRL', symbol: 'R$', label: 'Brazilian Real', locale: 'pt-BR' },
  MXN: { code: 'MXN', symbol: '$', label: 'Mexican Peso', locale: 'es-MX' },
  ARS: { code: 'ARS', symbol: '$', label: 'Argentine Peso', locale: 'es-AR' },
  CLP: { code: 'CLP', symbol: '$', label: 'Chilean Peso', locale: 'es-CL' },
  COP: { code: 'COP', symbol: '$', label: 'Colombian Peso', locale: 'es-CO' },
  PEN: { code: 'PEN', symbol: 'S/', label: 'Peruvian Sol', locale: 'es-PE' },
  ZAR: { code: 'ZAR', symbol: 'R', label: 'South African Rand', locale: 'en-ZA' },
  NGN: { code: 'NGN', symbol: '₦', label: 'Nigerian Naira', locale: 'en-NG' },
  EGP: { code: 'EGP', symbol: '£', label: 'Egyptian Pound', locale: 'ar-EG' },
  KES: { code: 'KES', symbol: 'KSh', label: 'Kenyan Shilling', locale: 'sw-KE' },
  MAD: { code: 'MAD', symbol: 'د.م.', label: 'Moroccan Dirham', locale: 'ar-MA' },
  TND: { code: 'TND', symbol: 'د.ت', label: 'Tunisian Dinar', locale: 'ar-TN' },
  XOF: { code: 'XOF', symbol: 'CFA', label: 'CFA Franc BCEAO', locale: 'fr-SN' },
  XAF: { code: 'XAF', symbol: 'FCFA', label: 'CFA Franc BEAC', locale: 'fr-CM' },
};

/**
 * Get currency info by code
 * @param {string} code - Currency code (e.g., 'EUR', 'USD')
 * @returns {Object|null} Currency info or null if not found
 */
export const getCurrencyInfo = (code) => {
  if (!code) return null;
  return SUPPORTED_CURRENCIES[code.toUpperCase()] || null;
};

/**
 * Get currency symbol by code
 * @param {string} code - Currency code
 * @returns {string} Currency symbol or code if not found
 */
export const getCurrencySymbol = (code) => {
  const info = getCurrencyInfo(code);
  return info ? info.symbol : code;
};

/**
 * Get currency label by code
 * @param {string} code - Currency code
 * @returns {string} Currency label or code if not found
 */
export const getCurrencyLabel = (code) => {
  const info = getCurrencyInfo(code);
  return info ? info.label : code;
};

/**
 * Format amount with currency
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code (default: 'EUR')
 * @param {string} locale - Locale for formatting (optional, uses currency default)
 * @param {Object} options - Additional Intl.NumberFormat options
 * @returns {string} Formatted currency string
 * 
 * @example
 * formatCurrency(1234.56, 'EUR') // '1.234,56 €'
 * formatCurrency(1234.56, 'USD', 'en-US') // '$1,234.56'
 */
export const formatCurrency = (amount, currencyCode = 'EUR', locale = null, options = {}) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '';
  }
  
  const currencyInfo = getCurrencyInfo(currencyCode);
  const formatLocale = locale || (currencyInfo ? currencyInfo.locale : 'en-US');
  const code = currencyInfo ? currencyInfo.code : currencyCode.toUpperCase();
  
  try {
    return new Intl.NumberFormat(formatLocale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...options,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${code}`;
  }
};

/**
 * Format amount with currency symbol only (no locale formatting)
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted string with symbol
 * 
 * @example
 * formatWithSymbol(1234.56, 'EUR') // '€1234.56'
 * formatWithSymbol(1234.56, 'USD') // '$1234.56'
 */
export const formatWithSymbol = (amount, currencyCode = 'EUR', decimals = 2) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '';
  }
  
  const symbol = getCurrencySymbol(currencyCode);
  return `${symbol}${amount.toFixed(decimals)}`;
};

/**
 * Parse currency string to number
 * @param {string} currencyString - Currency string to parse
 * @returns {number|null} Parsed number or null if invalid
 * 
 * @example
 * parseCurrency('$1,234.56') // 1234.56
 * parseCurrency('1.234,56 €') // 1234.56
 */
export const parseCurrency = (currencyString) => {
  if (!currencyString || typeof currencyString !== 'string') {
    return null;
  }
  
  const cleaned = currencyString
    .replace(/[^\d.,-]/g, '')
    .replace(/\.(?=.*\.)/g, '')
    .replace(/,(?=.*,)/g, '');
  
  const hasCommaDecimal = /,\d{1,2}$/.test(cleaned);
  const hasDotDecimal = /\.\d{1,2}$/.test(cleaned);
  
  let normalized;
  if (hasCommaDecimal && !hasDotDecimal) {
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (hasDotDecimal && !hasCommaDecimal) {
    normalized = cleaned.replace(/,/g, '');
  } else {
    normalized = cleaned.replace(/,/g, '');
  }
  
  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Get list of all supported currencies
 * @returns {Array} Array of currency objects with code, symbol, and label
 */
export const getCurrencyList = () => {
  return Object.values(SUPPORTED_CURRENCIES).map(currency => ({
    code: currency.code,
    symbol: currency.symbol,
    label: currency.label,
    displayLabel: `${currency.code} - ${currency.label} (${currency.symbol})`,
  }));
};

/**
 * Get currencies for a specific region
 * @param {string} region - Region code (e.g., 'europe', 'asia', 'americas', 'africa', 'middle_east')
 * @returns {Array} Array of currency objects for the region
 */
export const getCurrenciesByRegion = (region) => {
  const regionMap = {
    europe: ['EUR', 'GBP', 'CHF', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'HRK', 'RUB', 'UAH', 'TRY'],
    asia: ['JPY', 'CNY', 'HKD', 'SGD', 'INR', 'PKR', 'BDT', 'LKR', 'THB', 'MYR', 'IDR', 'PHP', 'VND', 'KRW', 'TWD'],
    americas: ['USD', 'CAD', 'BRL', 'MXN', 'ARS', 'CLP', 'COP', 'PEN'],
    oceania: ['AUD', 'NZD'],
    africa: ['ZAR', 'NGN', 'EGP', 'KES', 'MAD', 'TND', 'XOF', 'XAF'],
    middle_east: ['ILS', 'AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR'],
  };
  
  const codes = regionMap[region.toLowerCase()] || [];
  return codes.map(code => SUPPORTED_CURRENCIES[code]).filter(Boolean);
};

/**
 * Calculate VAT/tax amount
 * @param {number} netAmount - Net amount (before tax)
 * @param {number} vatRate - VAT rate as percentage (e.g., 20 for 20%)
 * @returns {Object} Object with netAmount, vatAmount, grossAmount
 * 
 * @example
 * calculateVAT(100, 20) // { netAmount: 100, vatAmount: 20, grossAmount: 120 }
 */
export const calculateVAT = (netAmount, vatRate) => {
  if (netAmount === null || netAmount === undefined || isNaN(netAmount)) {
    return { netAmount: 0, vatAmount: 0, grossAmount: 0 };
  }
  
  const rate = parseFloat(vatRate) || 0;
  const net = parseFloat(netAmount);
  const vatAmount = net * (rate / 100);
  const grossAmount = net + vatAmount;
  
  return {
    netAmount: Math.round(net * 100) / 100,
    vatAmount: Math.round(vatAmount * 100) / 100,
    grossAmount: Math.round(grossAmount * 100) / 100,
  };
};

/**
 * Calculate net amount from gross (reverse VAT calculation)
 * @param {number} grossAmount - Gross amount (including tax)
 * @param {number} vatRate - VAT rate as percentage
 * @returns {Object} Object with netAmount, vatAmount, grossAmount
 * 
 * @example
 * calculateNetFromGross(120, 20) // { netAmount: 100, vatAmount: 20, grossAmount: 120 }
 */
export const calculateNetFromGross = (grossAmount, vatRate) => {
  if (grossAmount === null || grossAmount === undefined || isNaN(grossAmount)) {
    return { netAmount: 0, vatAmount: 0, grossAmount: 0 };
  }
  
  const rate = parseFloat(vatRate) || 0;
  const gross = parseFloat(grossAmount);
  const netAmount = gross / (1 + rate / 100);
  const vatAmount = gross - netAmount;
  
  return {
    netAmount: Math.round(netAmount * 100) / 100,
    vatAmount: Math.round(vatAmount * 100) / 100,
    grossAmount: Math.round(gross * 100) / 100,
  };
};

/**
 * Calculate line item total
 * @param {number} quantity - Quantity
 * @param {number} unitPrice - Unit price
 * @param {number} vatRate - VAT rate as percentage (optional)
 * @param {number} discount - Discount percentage (optional)
 * @returns {Object} Object with subtotal, discountAmount, netAmount, vatAmount, totalAmount
 */
export const calculateLineItemTotal = (quantity, unitPrice, vatRate = 0, discount = 0) => {
  const qty = parseFloat(quantity) || 0;
  const price = parseFloat(unitPrice) || 0;
  const rate = parseFloat(vatRate) || 0;
  const discountRate = parseFloat(discount) || 0;
  
  const subtotal = qty * price;
  const discountAmount = subtotal * (discountRate / 100);
  const netAmount = subtotal - discountAmount;
  const vatAmount = netAmount * (rate / 100);
  const totalAmount = netAmount + vatAmount;
  
  return {
    subtotal: Math.round(subtotal * 100) / 100,
    discountAmount: Math.round(discountAmount * 100) / 100,
    netAmount: Math.round(netAmount * 100) / 100,
    vatAmount: Math.round(vatAmount * 100) / 100,
    totalAmount: Math.round(totalAmount * 100) / 100,
  };
};

/**
 * Common VAT rates by country
 */
export const VAT_RATES = {
  FR: { standard: 20, reduced: 10, superReduced: 5.5, zero: 0 },
  DE: { standard: 19, reduced: 7, zero: 0 },
  GB: { standard: 20, reduced: 5, zero: 0 },
  ES: { standard: 21, reduced: 10, superReduced: 4, zero: 0 },
  IT: { standard: 22, reduced: 10, superReduced: 4, zero: 0 },
  NL: { standard: 21, reduced: 9, zero: 0 },
  BE: { standard: 21, reduced: 12, superReduced: 6, zero: 0 },
  PT: { standard: 23, reduced: 13, superReduced: 6, zero: 0 },
  PL: { standard: 23, reduced: 8, superReduced: 5, zero: 0 },
  AT: { standard: 20, reduced: 13, superReduced: 10, zero: 0 },
  CH: { standard: 8.1, reduced: 2.6, zero: 0 },
  US: { standard: 0, state: 'varies', zero: 0 },
};

/**
 * Get VAT rates for a country
 * @param {string} countryCode - ISO 2-letter country code
 * @returns {Object|null} VAT rates object or null if not found
 */
export const getVATRates = (countryCode) => {
  if (!countryCode) return null;
  return VAT_RATES[countryCode.toUpperCase()] || null;
};

/**
 * Format VAT rate for display
 * @param {number|string} rate - VAT rate
 * @returns {string} Formatted VAT rate string
 * 
 * @example
 * formatVATRate(20) // '20%'
 * formatVATRate('NP') // 'NP' (Non-applicable)
 * formatVATRate('OO') // 'OO' (Out of scope)
 */
export const formatVATRate = (rate) => {
  if (rate === null || rate === undefined || rate === '') {
    return '';
  }
  
  if (typeof rate === 'string' && isNaN(parseFloat(rate))) {
    return rate;
  }
  
  const numRate = parseFloat(rate);
  if (isNaN(numRate)) {
    return String(rate);
  }
  
  return `${numRate}%`;
};

export default {
  SUPPORTED_CURRENCIES,
  VAT_RATES,
  getCurrencyInfo,
  getCurrencySymbol,
  getCurrencyLabel,
  formatCurrency,
  formatWithSymbol,
  parseCurrency,
  getCurrencyList,
  getCurrenciesByRegion,
  calculateVAT,
  calculateNetFromGross,
  calculateLineItemTotal,
  getVATRates,
  formatVATRate,
};
